const PROTECTION_STORAGE_KEY = 'bh2o_returns_protection_enabled';

class CartRedesignSummary extends HTMLElement {
  connectedCallback() {
    this.dataset.redoDebug = String(new URLSearchParams(window.location.search).get('redo') === '1');
    this.toggle = this.querySelector('[data-protection-toggle]');
    this.checkoutButton = this.querySelector('[data-secure-checkout]');
    this.onToggle = this.onToggle.bind(this);
    this.onCheckout = this.onCheckout.bind(this);
    this.onPageShow = () => this.resetCheckout();
    window.addEventListener('pageshow', this.onPageShow);
    this.onCartOpen = () => {
      this.redoPlacementAttempts = 0;
      this.refreshRedoPlacement();
    };
    this.cartDrawer = this.closest('cart-drawer-component');
    this.cartDrawer?.addEventListener('dialog:open', this.onCartOpen);
    this.redoPlacementAttempts = 0;

    this.toggle?.addEventListener('click', this.onToggle);
    this.checkoutButton?.addEventListener('click', this.onCheckout, true);
    this.render();
    this.redoStateTimer = setInterval(() => {
      this.refreshRedoPlacement();
      const state = JSON.stringify([window.redoStorefront?.coverageInfo, window.redoStorefront?.coverageNotShowingReason, this.protectionAvailable]);
      if (state !== this.redoState || this.renderedRedoUnavailable !== this.redoUnavailable) {
        this.redoState = state;
        this.renderedRedoUnavailable = this.redoUnavailable;
        this.render();
      }
    }, 250);
  }

  disconnectedCallback() {
    this.resetCheckout();
    window.removeEventListener('pageshow', this.onPageShow);
    clearInterval(this.redoStateTimer);
    this.cartDrawer?.removeEventListener('dialog:open', this.onCartOpen);
    this.toggle?.removeEventListener('click', this.onToggle);
    this.checkoutButton?.removeEventListener('click', this.onCheckout, true);
  }

  get protectionEnabled() {
    if (this._protectionEnabled !== undefined) return this._protectionEnabled;
    try {
      return window.localStorage.getItem(PROTECTION_STORAGE_KEY) !== 'false';
    } catch (_error) {
      return true;
    }
  }

  set protectionEnabled(enabled) {
    this._protectionEnabled = enabled;
    try {
      window.localStorage.setItem(PROTECTION_STORAGE_KEY, enabled ? 'true' : 'false');
    } catch (_error) {
      // The cart remains usable when storage is unavailable.
    }
  }

  formatMoney(cents) {
    const currency = this.dataset.currency || window.Shopify?.currency?.active || 'AUD';
    const locale = currency === 'AUD' ? 'en-AU' : document.documentElement.lang || 'en';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  get offeredCoverage() {
    const info = window.redoStorefront?.coverageInfo;
    if (info?.both?.cartEligible) return info.both;
    if (info?.packageProtection?.cartEligible) return info.packageProtection;
    return info?.both ?? info?.packageProtection ?? null;
  }

  get coverage() {
    const redo = window.redoStorefront;
    const unavailable = { cartEligible: false, price: 0 };
    const reason = redo?.coverageNotShowingReason;
    if (this.redoUnavailable || (reason && reason !== 'NO_TOGGLE_LOCATIONS_FOUND')) return unavailable;
    // Keep vendor blocking reasons authoritative: eligibility alone does not
    // guarantee that Redo has a purchasable standalone product configured.
    return this.offeredCoverage;
  }

  refreshRedoPlacement() {
    if (!this.offeredCoverage?.cartEligible || !this.closest('dialog')?.open) return;
    if (this.findRedoButton('coverage-button')) {
      this.redoUnavailable = false;
      return;
    }
    if (this.redoPlacementAttempts >= 5 || Date.now() - (this.lastRedoPlacementRefresh || 0) < 1000) return;
    this.redoPlacementAttempts++;
    this.lastRedoPlacementRefresh = Date.now();
    // Redo's own placement refresh re-queries the checkout selector after cart rendering.
    document.dispatchEvent(new CustomEvent('redo:re-render-elements'));
  }

  get protectionAvailable() {
    return this.coverage?.cartEligible === true
      && !!this.findRedoButton('coverage-button')
      && !!this.findRedoButton('non-coverage-button');
  }

  render() {
    const coverage = this.coverage;
    const available = this.protectionAvailable;
    const includesReturns = window.redoStorefront?.coverageInfo?.both?.cartEligible === true;
    const protectionTitle = includesReturns ? 'Free returns + package protection' : 'Package protection';
    const title = this.querySelector('.bh2o-cart-protection__copy strong');
    if (title) title.textContent = protectionTitle;
    this.dataset.protectionAvailable = String(available);
    const enabled = available && this.protectionEnabled;
    const cartTotal = Number(this.dataset.cartTotal) || 0;
    const displaySubtotal = Number(this.dataset.displaySubtotal) || cartTotal;
    const shipping = Number(this.dataset.shippingCost) || 0;
    const protection = enabled ? Number(coverage?.price) || 0 : 0;
    const finalTotal = cartTotal + shipping + protection;
    const originalTotal = displaySubtotal + shipping + protection;

    if (this.toggle) {
      this.toggle.setAttribute('aria-label', includesReturns ? 'Free returns and package protection' : 'Package protection');
      this.toggle.setAttribute('aria-checked', String(enabled));
      this.toggle.disabled = !available;
    }
    const protectionCopy = this.querySelector('.bh2o-cart-protection__copy small');
    if (protectionCopy) {
      protectionCopy.textContent = !available
        ? 'Not available for this cart or location'
        : coverage ? `Add for ${this.formatMoney(coverage.price)}` : 'Checking availability…';
    }

    const breakdownTotal = this.querySelector('[data-breakdown-total]');
    const footerTotal = this.querySelector('[data-final-total]');
    const originalTotalElement = this.querySelector('[data-original-total]');
    const shippingLabel = this.querySelector('[data-shipping-label]');
    const shippingTotal = this.querySelector('[data-shipping-total]');
    const shippingRow = this.querySelector('[data-shipping-row]');
    if (shippingRow) shippingRow.hidden = !available;
    if (breakdownTotal) breakdownTotal.textContent = this.formatMoney(finalTotal);
    if (footerTotal) footerTotal.textContent = this.formatMoney(finalTotal);
    if (originalTotalElement) originalTotalElement.textContent = this.formatMoney(originalTotal);
    if (shippingLabel) {
      shippingLabel.textContent = enabled ? 'Shipping + Package protection' : 'Shipping';
    }
    if (shippingTotal) {
      const shippingAndProtection = shipping + protection;
      shippingTotal.textContent = shippingAndProtection === 0 ? 'FREE' : this.formatMoney(shippingAndProtection);
    }
  }

  onToggle() {
    this.protectionEnabled = !this.protectionEnabled;
    this.querySelector('[data-checkout-error]')?.remove();
    this.render();
  }

  findRedoButton(target) {
    const cart = this.closest('cart-drawer-component') || this;
    const localWidgets = [...cart.querySelectorAll('redo-shopify-toggle')];
    // Some Redo attachment strategies place the widget outside the cart footer.
    const widgets = [...new Set([...localWidgets, ...document.querySelectorAll('redo-shopify-toggle')])];
    for (const widget of widgets) {
      const root = widget.root || widget.shadowRoot || widget;
      const button = root.querySelector(`[data-target="${target}"]`);
      if (button && !button.disabled && button.getAttribute('aria-disabled') !== 'true') return button;
    }
    return null;
  }

  resetCheckout() {
    clearTimeout(this.checkoutRecoveryTimer);
    this.checkoutPending = false;
    this.checkoutButton?.removeAttribute('aria-busy');
    this.checkoutButton?.removeAttribute('aria-disabled');
    const label = this.checkoutButton?.querySelector('span:last-child');
    if (label && this.originalCheckoutLabel != null) label.textContent = this.originalCheckoutLabel;
  }

  async onCheckout(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.checkoutPending) return;
    this.querySelector('[data-checkout-error]')?.remove();
    this.checkoutPending = true;
    this.checkoutButton.setAttribute('aria-busy', 'true');
    this.checkoutButton.setAttribute('aria-disabled', 'true');
    const label = this.checkoutButton.querySelector('span:last-child');
    this.originalCheckoutLabel = label?.textContent;
    if (label) label.textContent = 'Preparing checkout…';

    try {
      // Keep feedback visible after the hand-off, but allow retry if navigation stalls.
      this.checkoutRecoveryTimer = setTimeout(() => this.resetCheckout(), 30000);
      if (this.coverage?.cartEligible === false) {
        window.location.assign('/checkout');
        return;
      }
      this.redoPlacementAttempts = 0;
      this.refreshRedoPlacement();
      const deadline = Date.now() + 10000;
      do {
        if (!this.isConnected) return;
        if (this.coverage?.cartEligible === false) {
          this.render();
          window.location.assign('/checkout');
          return;
        }
        const target = this.protectionEnabled ? 'coverage-button' : 'non-coverage-button';
        const redoButton = this.findRedoButton(target);
        if (redoButton) {
          redoButton.click();
          return;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      } while (Date.now() < deadline);

      if (!this.isConnected) return;
      this.redoUnavailable = true;
      this.render();
      window.location.assign('/checkout');
    } catch (error) {
      this.resetCheckout();
      throw error;
    } finally {
      if (!this.isConnected) this.resetCheckout();
    }
  }
}

if (!customElements.get('cart-redesign-summary')) {
  customElements.define('cart-redesign-summary', CartRedesignSummary);
}
